// src/hooks/useServices.js - FIXED Firebase Integration
import { useCallback, useEffect, useState } from 'react';
import { Service } from '../models/Service';
import { FirebaseService } from '../services/FirebaseService';
import { useAuth } from './useAuth';

export const useServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  // FIXED: Load services from Firebase
  const loadServices = useCallback(async () => {
    if (!user?.businessId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 Loading services for business:', user.businessId);
      
      // FIXED: FirebaseService.getBusinessServices now returns simple object
      const firebaseServices = await FirebaseService.getBusinessServices(user.businessId);
      
      console.log('📊 Firebase services loaded:', firebaseServices);
      
      // FIXED: Handle both object and array responses
      let serviceModels = [];
      
      if (firebaseServices && typeof firebaseServices === 'object') {
        if (Array.isArray(firebaseServices)) {
          // If it's an array, use it directly
          serviceModels = firebaseServices.map(serviceData => 
            Service.fromFirebaseData(serviceData.id || `service_${Date.now()}`, user.businessId, serviceData)
          );
        } else {
          // If it's an object, convert to array
          serviceModels = Object.entries(firebaseServices).map(([id, data]) => 
            Service.fromFirebaseData(id, user.businessId, data)
          );
        }
      }
      
      // FIXED: Add default services if none exist
      if (serviceModels.length === 0) {
        console.log('📝 No services found, creating default services');
        serviceModels = [
          Service.createCustomService(user.businessId, {
            name: 'Full Service',
            description: 'Complete service package',
            category: 'general',
            duration: 60,
            price: 40,
            active: true
          }),
          Service.createCustomService(user.businessId, {
            name: 'Consultation',
            description: 'Initial consultation session',
            category: 'consultation',
            duration: 30,
            price: 25,
            active: true
          }),
          Service.createCustomService(user.businessId, {
            name: 'Quick Service',
            description: 'Express service option',
            category: 'express',
            duration: 30,
            price: 30,
            active: true
          })
        ];
        
        // Save default services to Firebase
        try {
          for (const service of serviceModels) {
            await FirebaseService.createBusinessService(user.businessId, service.toFirebaseData());
          }
          console.log('✅ Default services created successfully');
        } catch (saveError) {
          console.warn('⚠️ Could not save default services:', saveError);
        }
      }
      
      setServices(serviceModels);
      console.log(`✅ Services loaded successfully: ${serviceModels.length} services`);
      
    } catch (err) {
      console.error('❌ Failed to load services:', err);
      setError(err.message);
      
      // FIXED: Provide fallback services on error
      const fallbackServices = [
        Service.createCustomService(user.businessId, {
          name: 'Standard Service',
          description: 'Default service option',
          category: 'general',
          duration: 60,
          price: 50,
          active: true
        })
      ];
      setServices(fallbackServices);
    } finally {
      setLoading(false);
    }
  }, [user?.businessId]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  // FIXED: Add new service with proper validation
  const addService = async (serviceModel) => {
    try {
      setError(null);
      
      // Validate service model
      const validation = serviceModel.validate();
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      console.log('📝 Adding new service:', serviceModel.name);

      // FIXED: Use createBusinessService instead of addBusinessService
      const result = await FirebaseService.createBusinessService(user.businessId, serviceModel.toFirebaseData());
      
      if (result.success) {
        await loadServices(); // Reload services
        console.log('✅ Service added successfully');
        return { success: true };
      } else {
        throw new Error(result.error || 'Failed to add service');
      }
    } catch (error) {
      console.error('❌ Failed to add service:', error);
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  // FIXED: Update existing service
  const updateService = async (serviceId, updates) => {
    try {
      setError(null);
      
      const existingService = services.find(s => s.id === serviceId);
      if (!existingService) {
        throw new Error('Service not found');
      }

      console.log('📝 Updating service:', serviceId);

      // Update the model
      const updatedService = { ...existingService, ...updates };
      const serviceModel = Service.fromFirebaseData(serviceId, user.businessId, updatedService);
      
      const validation = serviceModel.validate();
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      await FirebaseService.updateBusinessService(user.businessId, serviceId, serviceModel.toFirebaseData());
      await loadServices(); // Reload services
      
      console.log('✅ Service updated successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ Failed to update service:', error);
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  // FIXED: Delete service
  const deleteService = async (serviceId) => {
    try {
      setError(null);
      
      console.log('🗑️ Deleting service:', serviceId);
      
      await FirebaseService.deleteBusinessService(user.businessId, serviceId);
      await loadServices(); // Reload services
      
      console.log('✅ Service deleted successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ Failed to delete service:', error);
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  // Get services by category
  const getServicesByCategory = useCallback((category) => {
    return services.filter(service => service.category === category);
  }, [services]);

  // Get active services only
  const getActiveServices = useCallback(() => {
    return services.filter(service => service.active);
  }, [services]);

  // Get service by name (useful for Quick Booking)
  const getServiceByName = useCallback((serviceName) => {
    return services.find(service => service.name === serviceName);
  }, [services]);

  // FIXED: Get services formatted for dropdowns
  const getServicesForDropdown = useCallback(() => {
    return services.map(service => ({
      id: service.id,
      name: service.name,
      price: service.price,
      duration: service.duration,
      description: service.description
    }));
  }, [services]);

  return {
    services,
    loading,
    error,
    addService,
    updateService,
    deleteService,
    getServicesByCategory,
    getActiveServices,
    getServiceByName,
    getServicesForDropdown,
    reload: loadServices,
    // Helper properties for debugging
    hasServices: services.length > 0,
    serviceCount: services.length
  };
};